import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, CameraOff, Footprints, HeartPulse, Loader2, Printer } from 'lucide-react';
import { CHEMINS, groupesDeLaJournee } from '../../../lib/navigation/chemins';
import { lireAffectations, lireEssentiels, lireInscrits, lireJournees } from '../../../lib/jpa/gateway.supabase';
import { dateLongue, heure, regrouperParClasse, type InscritJpa, type JourneeJpa } from '../../../lib/jpa/regles';
import { classeCourte, classesDuGroupe, trierGroupes, trierParNom } from '../../../lib/jpa/groupes';
import { lireSousGroupes, type SousGroupe } from '../../../lib/groupes/groupes.supabase';
import {
  indicateurs,
  lignesASavoir,
  personnesEnClair,
  type EssentielsDeLEnfant,
  type LigneASavoir,
} from '../../../lib/presences/essentiels';

/*
  Les listes d'une journée pédagogique, à imprimer depuis le navigateur : une
  feuille A4 par groupe (ou par classe, tant qu'aucun groupe n'est formé).

  - `?liste=presence` : la liste de présence — enfant, classe, arrivée, et le
    départ avant la fin des activités (heure, avec qui), à remplir seulement
    pour un enfant qui part plus tôt ; deux lignes pour l'enfant présent sans
    inscription. **Aucune information de santé** (décidé par Éclosion le
    03/11/2026) : la feuille reste à l'accueil, à la vue de tous. Seuls « pas
    de photo » et « rentre seul » s'y montrent.
  - `?liste=sante` : la liste santé du groupe, confidentielle — allergies,
    régime, médicaments, à savoir, le départ (rentre seul à quelle heure, ou
    avec qui) et le parent à appeler, avec le cœur « santé à savoir ».

  Les pictogrammes sont ceux des listes en ligne des stages, et ce qui se
  montre suit les règles de `presences/essentiels.ts` : la feuille et l'écran
  disent la même chose.

  Cette page vit hors de la mise en page de l'administration : sans menu ni
  barre, ce qui s'imprime est la liste et rien d'autre. Un enfant sans groupe
  n'est jamais oublié : il a sa feuille, « Sans groupe », à la fin.
*/

type Liste = 'presence' | 'sante';
type Par = 'groupe' | 'classe';

interface Feuille {
  cle: string;
  titre: string;
  /** « M1A (14) · M1B (11) » : les classes d'un groupe ; rien pour une feuille de classe. */
  classes: string | null;
  inscrits: InscritJpa[];
  sansGroupe?: boolean;
}

const icone = 'h-4 w-4 flex-shrink-0 inline-block';
const SANTE: LigneASavoir['cle'][] = ['allergies', 'regime', 'medicaments', 'a_savoir'];

/** Les pictogrammes devant le nom ; le cœur « santé » sur la liste santé seulement. */
function Signes({ e, sante }: { e: EssentielsDeLEnfant | undefined; sante: boolean }) {
  const s = indicateurs(e);
  const coeur = sante && s.sante;
  if (!coeur && !s.pasDePhoto && e?.depart.partSeul !== true) return null;
  return (
    <span className="ml-1 inline-flex items-center gap-1 align-[-2px]">
      {coeur && <HeartPulse className={`${icone} text-red-600`} aria-label="Santé : à savoir" />}
      {s.pasDePhoto && <CameraOff className={`${icone} text-amber-700`} aria-label="Pas de photo" />}
      {e?.depart.partSeul === true && <Footprints className={`${icone} text-green-700`} aria-label="Rentre seul" />}
    </span>
  );
}

function Legende({ sante }: { sante: boolean }) {
  return (
    <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700">
      {sante && <span className="inline-flex items-center gap-1"><HeartPulse className={`${icone} text-red-600`} aria-hidden="true" /> santé : à savoir</span>}
      <span className="inline-flex items-center gap-1"><CameraOff className={`${icone} text-amber-700`} aria-hidden="true" /> pas de photo</span>
      <span className="inline-flex items-center gap-1"><Footprints className={`${icone} text-green-700`} aria-hidden="true" /> rentre seul</span>
    </p>
  );
}

function LignesDeSante({ e }: { e: EssentielsDeLEnfant | undefined }) {
  const lignes = e ? lignesASavoir(e).filter((l) => SANTE.includes(l.cle)) : [];
  if (lignes.length === 0) return <span className="text-gray-500">Rien de signalé</span>;
  return (
    <>
      {lignes.map((l) => (
        <span key={l.cle} className={clsx('block', l.alerte && 'font-bold text-red-800')}>{l.libelle} : {l.texte}</span>
      ))}
    </>
  );
}

/**
 * Le départ, pour la liste santé : rentre seul, et à quelle heure ; sinon
 * l'heure habituelle et les personnes autorisées de la fiche (comme la fiche
 * santé PDF des stages : les parents, plus ces personnes-là).
 */
function Depart({ e }: { e: EssentielsDeLEnfant | undefined }) {
  if (!e) return <span className="text-gray-500">—</span>;
  const { partSeul, heure: h, personnes } = e.depart;
  if (partSeul === true) {
    return <span className="font-bold text-green-800">Rentre seul·e{h ? ` à ${h}` : ' (heure non indiquée)'}</span>;
  }
  if (!h && personnes.length === 0) {
    return <span className="text-gray-500">{partSeul === false ? 'Avec les parents' : '—'}</span>;
  }
  return (
    <>
      {h && <span className="block">Vers {h}</span>}
      {personnes.length > 0 && <span className="block">Pers. autorisées : {personnesEnClair(personnes)}</span>}
    </>
  );
}

export default function AdminJourneeListesPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const [recherche, setRecherche] = useSearchParams();
  const [journee, setJournee] = useState<JourneeJpa | null>(null);
  const [inscrits, setInscrits] = useState<InscritJpa[]>([]);
  const [groupes, setGroupes] = useState<SousGroupe[]>([]);
  const [affectations, setAffectations] = useState<Map<string, string | null>>(new Map());
  const [essentiels, setEssentiels] = useState<Map<string, EssentielsDeLEnfant>>(new Map());
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    if (!sessionId) return;
    let annule = false;
    void (async () => {
      try {
        const j = (await lireJournees()).find((x) => x.sessionId === sessionId) ?? null;
        if (!j) throw new Error('Cette journée est introuvable.');
        const lus = (await lireInscrits([sessionId])).filter((i) => i.vivante);
        const [g, a, e] = await Promise.all([lireSousGroupes([sessionId]), lireAffectations(sessionId), lireEssentiels(lus)]);
        if (annule) return;
        setJournee(j);
        setInscrits(trierParNom(lus));
        setGroupes(trierGroupes(g));
        setAffectations(a);
        setEssentiels(e);
      } catch (err) {
        if (!annule) setErreur(err instanceof Error ? err.message : String(err));
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => { annule = true; };
  }, [sessionId]);

  if (chargement) {
    return <div className="flex justify-center items-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }
  if (erreur || !journee) {
    return (
      <div className="max-w-xl mx-auto p-8">
        <p className="text-red-700 mb-4">{erreur ?? 'Cette journée est introuvable.'}</p>
        <Link to={CHEMINS.planification.jpa} className="btn-outline">Retour aux journées</Link>
      </div>
    );
  }

  const ids = new Set(groupes.map((g) => g.id));
  const groupeDe = (registrationId: string) => {
    const g = affectations.get(registrationId) ?? null;
    return g && ids.has(g) ? g : null;
  };
  const groupesFormes = groupes.some((g) => inscrits.some((i) => groupeDe(i.registrationId) === g.id));
  const liste: Liste = recherche.get('liste') === 'sante' ? 'sante' : 'presence';
  const parDemande = recherche.get('par');
  const par: Par = parDemande === 'classe' || (parDemande !== 'groupe' && !groupesFormes) ? 'classe' : 'groupe';
  const changer = (cle: 'liste' | 'par', valeur: string) => {
    const suite = new URLSearchParams(recherche);
    suite.set(cle, valeur);
    setRecherche(suite, { replace: true });
  };

  const sansGroupe = inscrits.filter((i) => groupeDe(i.registrationId) === null);
  const feuilles: Feuille[] = par === 'classe'
    ? regrouperParClasse(inscrits).map((c) => ({ cle: c.classe, titre: c.classe, classes: null, inscrits: c.inscrits }))
    : [
        ...groupes.flatMap((g) => {
          const leurs = inscrits.filter((i) => groupeDe(i.registrationId) === g.id);
          if (leurs.length === 0) return [];
          const classes = classesDuGroupe(leurs).map((c) => `${c.libelle} (${c.nombre})`).join(' · ');
          return [{ cle: g.id, titre: g.nom, classes, inscrits: leurs }];
        }),
        ...(sansGroupe.length > 0
          ? [{
              cle: 'sans-groupe',
              titre: 'Sans groupe',
              classes: classesDuGroupe(sansGroupe).map((c) => `${c.libelle} (${c.nombre})`).join(' · '),
              inscrits: sansGroupe,
              sansGroupe: true,
            }]
          : []),
      ];

  const imprimeLe = new Intl.DateTimeFormat('fr-BE', { dateStyle: 'full', timeStyle: 'short', timeZone: 'Europe/Brussels' }).format(new Date());
  const segment = (actif: boolean) => clsx(
    'min-h-[40px] px-3 text-sm font-semibold border first-of-type:rounded-l-lg last-of-type:rounded-r-lg',
    actif ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-primary-300',
  );
  /*
    Une feuille par groupe, et si possible une page : au-delà de 20 enfants
    (liste de présence) ou de 18 (liste santé, aux lignes plus hautes), les
    lignes se resserrent — un groupe de 25 tient sur sa page A4 ; en deçà,
    elles gardent de la place pour écrire.
  */
  const celluleDe = (f: Feuille) => clsx(
    'border border-gray-300 px-2 align-top leading-tight',
    liste === 'presence' ? (f.inscrits.length > 20 ? 'py-1' : 'py-2') : (f.inscrits.length > 18 ? 'py-[3px]' : 'py-2'),
  );
  const finDesActivites = heure(journee.activitesFin);
  const entete = 'border border-primary-200 px-2 py-1 text-left';

  return (
    <div className="bg-gray-100 min-h-screen print:bg-white">
      <style>{'@page { size: A4 portrait; margin: 10mm; }'}</style>
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-[210mm] mx-auto px-4 py-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to={groupesDeLaJournee(journee.sessionId)} className="text-sm font-semibold text-primary-700 hover:underline">
              ← Groupes de la journée
            </Link>
            <p className="text-sm text-gray-700">
              {feuilles.length} feuille{feuilles.length > 1 ? 's' : ''} · {inscrits.length} enfant{inscrits.length > 1 ? 's' : ''}
            </p>
            <button type="button" onClick={() => window.print()} disabled={feuilles.length === 0} className="btn-primary flex items-center disabled:opacity-50">
              <Printer className="h-4 w-4 mr-2" aria-hidden="true" />
              Imprimer
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div role="group" aria-label="La liste" className="inline-flex">
              <button type="button" aria-pressed={liste === 'presence'} onClick={() => changer('liste', 'presence')} className={segment(liste === 'presence')}>Présences</button>
              <button type="button" aria-pressed={liste === 'sante'} onClick={() => changer('liste', 'sante')} className={segment(liste === 'sante')}>Santé</button>
            </div>
            {groupesFormes ? (
              <div role="group" aria-label="Une feuille par" className="inline-flex">
                <button type="button" aria-pressed={par === 'groupe'} onClick={() => changer('par', 'groupe')} className={segment(par === 'groupe')}>Par groupe</button>
                <button type="button" aria-pressed={par === 'classe'} onClick={() => changer('par', 'classe')} className={segment(par === 'classe')}>Par classe</button>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                Une feuille par classe : aucun groupe formé.{' '}
                <Link to={groupesDeLaJournee(journee.sessionId)} className="font-semibold text-primary-700 hover:underline">Former les groupes</Link>
              </p>
            )}
          </div>
          {par === 'groupe' && sansGroupe.length > 0 && (
            <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" role="status">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>
                {sansGroupe.length === 1 ? '1 enfant n’est' : `${sansGroupe.length} enfants ne sont`} dans aucun groupe : imprimé{sansGroupe.length > 1 ? 's' : ''} sur la feuille « Sans groupe », à la fin.{' '}
                <Link to={groupesDeLaJournee(journee.sessionId)} className="font-semibold underline">Les placer d’abord</Link>
              </span>
            </p>
          )}
        </div>
      </div>

      {feuilles.length === 0 && (
        <p className="max-w-[210mm] mx-auto p-8 text-gray-700">Aucun inscrit pour cette journée.</p>
      )}

      {feuilles.map((f, n) => {
        const cellule = celluleDe(f);
        return (
        <section
          key={f.cle}
          className="bg-white max-w-[210mm] mx-auto my-6 p-[12mm] shadow print:shadow-none print:my-0 print:p-0 print:max-w-none [print-color-adjust:exact] [-webkit-print-color-adjust:exact]"
          style={{ breakAfter: n < feuilles.length - 1 ? 'page' : 'auto' }}
        >
          {/* Pas de <header> : la feuille de style d'impression de l'application les masque tous (index.css). */}
          <div className="flex justify-between items-start gap-4 border-b-[3px] border-primary-600 pb-2 mb-2">
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-primary-700">
                {liste === 'sante' ? 'Liste santé · confidentiel' : 'Liste de présence'} · journée pédagogique animée
              </p>
              <h1 className="text-xl font-extrabold mt-0.5">
                {f.titre}{' '}
                <span className="text-sm font-bold text-gray-600">· {f.inscrits.length} enfant{f.inscrits.length > 1 ? 's' : ''}</span>
              </h1>
              <p className="text-sm first-letter:uppercase">{dateLongue(journee.date)} · {journee.ecole ?? journee.centre}</p>
              {f.classes && <p className="text-xs text-gray-600">Classes : {f.classes}</p>}
            </div>
            <div className="text-right text-xs text-gray-700 leading-snug">
              <strong className="text-sm text-gray-900">Éclosion ASBL</strong><br />
              Accueil {heure(journee.accueilDebut)} – {heure(journee.accueilFin)}<br />
              Activités {heure(journee.activitesDebut)} – {heure(journee.activitesFin)}
            </div>
          </div>

          {f.sansGroupe && (
            <p className="mb-2 rounded border border-amber-400 bg-amber-50 px-2 py-1 text-sm font-semibold text-amber-900">
              Ces enfants n’étaient dans aucun groupe au moment de l’impression : à placer dans un groupe.
            </p>
          )}
          <div className="mb-1.5 flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
            {liste === 'presence' && (
              <p className="text-sm">Animateurs : <span className="inline-block w-[85mm] border-b border-gray-400">&nbsp;</span></p>
            )}
            <Legende sante={liste === 'sante'} />
          </div>

          {liste === 'presence' ? (
            <table className="w-full border-collapse text-[14px] leading-tight">
              <thead>
                <tr className="bg-primary-50">
                  <th className={clsx(entete, par === 'groupe' ? 'w-[38%]' : 'w-[44%]')}>Enfant</th>
                  {par === 'groupe' && <th className={clsx(entete, 'w-[8%]')}>Classe</th>}
                  <th className={clsx(entete, 'w-[14%]')}>Arrivée</th>
                  <th className={entete}>
                    Départ avant {finDesActivites}
                    <span className="block text-[11px] font-normal text-gray-600">seulement si l’enfant part plus tôt : heure, avec qui</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {f.inscrits.map((i) => (
                  <tr key={i.registrationId} className="[break-inside:avoid]">
                    <td className={clsx(cellule, 'font-bold')}>{i.nom} {i.prenom}<Signes e={essentiels.get(i.registrationId)} sante={false} /></td>
                    {par === 'groupe' && <td className={cellule}>{classeCourte(i.classe)}</td>}
                    <td className={cellule}></td>
                    <td className={cellule}></td>
                  </tr>
                ))}
                {[0, 1].map((k) => (
                  <tr key={`vide-${k}`} className="[break-inside:avoid]">
                    <td className={clsx(cellule, 'text-sm text-gray-500')}>Inscrit sur place :</td>
                    {par === 'groupe' && <td className={cellule}></td>}
                    <td className={cellule}></td>
                    <td className={cellule}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full border-collapse text-[12.5px] leading-tight">
              <thead>
                <tr className="bg-primary-50">
                  <th className={clsx(entete, 'w-[24%]')}>Enfant</th>
                  <th className={clsx(entete, 'w-[8%]')}>Classe</th>
                  <th className={entete}>Allergies · régime · médicaments · à savoir</th>
                  <th className={clsx(entete, 'w-[18%]')}>Départ</th>
                  <th className={clsx(entete, 'w-[18%]')}>Parent · téléphone</th>
                </tr>
              </thead>
              <tbody>
                {f.inscrits.map((i) => {
                  const e = essentiels.get(i.registrationId);
                  return (
                    <tr key={i.registrationId} className="[break-inside:avoid]">
                      <td className={clsx(cellule, 'font-bold text-[14px]')}>{i.nom} {i.prenom}<Signes e={e} sante /></td>
                      <td className={cellule}>{classeCourte(i.classe)}</td>
                      <td className={cellule}><LignesDeSante e={e} /></td>
                      <td className={cellule}><Depart e={e} /></td>
                      <td className={cellule}>
                        <span className="block">{i.parent}</span>
                        {i.parentTelephone && <span className="block font-semibold whitespace-nowrap">{i.parentTelephone}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <p className="text-xs text-gray-700 mt-2">
            <strong>{f.inscrits.length} enfant{f.inscrits.length > 1 ? 's' : ''}</strong>{' '}
            {par === 'classe' ? 'dans cette classe' : f.sansGroupe ? 'sans groupe' : 'dans ce groupe'}.
            {liste === 'presence'
              ? ` Départ : à remplir seulement si l’enfant part avant ${finDesActivites}. Un enfant présent sans inscription : l’inscrire ici ; la coordination l’encode ensuite.`
              : ' Confidentiel : à garder par l’équipe, hors de la vue de tous. Le détail complet est sur la fiche de l’enfant ; en cas de doute, appeler le parent, puis la coordination.'}
          </p>
          <footer className="flex justify-between gap-3 border-t border-gray-200 mt-2 pt-1.5 text-[11px] text-gray-600">
            <span>Coordination : {journee.telephoneCoordination}</span>
            <span>Imprimée le {imprimeLe} · feuille {n + 1} sur {feuilles.length}</span>
          </footer>
        </section>
        );
      })}
    </div>
  );
}
